import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ContextTabs, ViewToggle, RecordCard, CompactRow, DataTable, StatusBadge, MetricCard,
  DetailDrawer, ActionBar, ActionSheet, LoadingState, EmptyState, ErrorState, UnknownState, ConfirmModal,
} from './osPrimitives';
import { Search, ChevronRight, Building2, Car, User, Shield, Wallet, Boxes, GitBranch, ArrowUpRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTHZ = 'command_center';
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const authHeaders = (json) => { const t = localStorage.getItem('ace_admin_token'); return { ...(json ? { 'Content-Type': 'application/json' } : {}), ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders(false) });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}
async function postJSON(path, body) {
  const r = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include', headers: authHeaders(true), body: JSON.stringify(body || {}) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `Action failed (${r.status})`);
  return j;
}

const TABS = [
  { id: 'supply', label: 'Supply' }, { id: 'fleet', label: 'Fleet' }, { id: 'drivers', label: 'Drivers' },
  { id: 'partners', label: 'Partners' }, { id: 'fulfillment', label: 'Fulfillment' },
  { id: 'compliance', label: 'Compliance' }, { id: 'payouts', label: 'Payouts' },
];

// ── compliance / reconciliation status → OS status color law ──
const REC_STATUS = { NOT_READY: 'unknown', READY: 'waiting', HOLD_REVIEW: 'attention', DISPUTED: 'risk', APPROVED: 'ready', SCHEDULED: 'waiting', PAID: 'ready' };
const CMP_STATUS = { current: 'ready', approved: 'ready', valid: 'ready', expiring: 'attention', under_review: 'attention', uploaded: 'attention', expired: 'risk', rejected: 'risk' };

export default function NetworkLive() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('payouts') ? 'payouts' : (params.get('net') || params.get('tab') || 'supply');
  const [tab, setTab] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : 'supply');
  const [confirm, setConfirm] = useState(null); // { title, body, danger, onConfirm }

  // shared material-action runner
  const doConfirm = (cfg) => setConfirm(cfg);
  const runAction = useCallback(async (path, body, onDone) => {
    try { await postJSON(path, body); toast.success('Done'); onDone && onDone(); }
    catch (e) { toast.error(e.message); }
  }, []);

  return (
    <div className="os-animate" data-testid="network-live">
      <ContextTabs tabs={TABS} active={tab} onChange={(t) => { setTab(t); }} testid="network-tabs" />
      <div className="mt-4">
        {tab === 'supply' && <SupplyView />}
        {tab === 'fleet' && <FleetView />}
        {tab === 'drivers' && <DriversView />}
        {tab === 'partners' && <PartnersView doConfirm={doConfirm} runAction={runAction} />}
        {tab === 'fulfillment' && <FulfillmentView doConfirm={doConfirm} runAction={runAction} bk={params.get('bk')} frq={params.get('frq')} navigate={navigate} />}
        {tab === 'compliance' && <ComplianceView doConfirm={doConfirm} runAction={runAction} />}
        {tab === 'payouts' && <PayoutsView doConfirm={doConfirm} runAction={runAction} openRec={params.get('rec')} initialFilter={params.get('payouts')} />}
      </div>
      <ConfirmModal open={!!confirm} title={confirm?.title} body={confirm?.body} danger={confirm?.danger}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        onConfirm={() => { const c = confirm; setConfirm(null); c?.onConfirm && c.onConfirm(); }}
        onClose={() => setConfirm(null)} testid="network-confirm" />
    </div>
  );
}

function useLoader(path) {
  const [state, setState] = useState({ loading: true });
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: false }));   // keep previous data during reload
    getJSON(path).then((d) => setState({ loading: false, data: d })).catch(() => setState((s) => ({ ...s, loading: false, error: true })));
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

/* ══════════════ SUPPLY ══════════════ */
function SupplyView() {
  const { loading, data, error, reload } = useLoader('/api/fulfillment/control/supply');
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Supply overview is unavailable." onRetry={reload} />;
  const wf = data.waterfall || [];
  const sources = [
    { key: 'ace', label: 'ACE Direct', icon: Boxes, data: data.ace_direct, direct: true },
    { key: 'preferred', label: 'Preferred Affiliates', icon: Building2, data: data.preferred_affiliate },
    { key: 'collective', label: 'Driven Collective', icon: Building2, data: data.driven_collective },
    { key: 'special', label: 'Special Source', icon: Building2, data: data.special_source },
  ];
  return (
    <div data-testid="supply-view">
      {/* Waterfall (P6 authoritative order — not ranked in React) */}
      <div className="os-card p-4 mb-4" data-testid="supply-waterfall">
        <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Fulfillment waterfall</p>
        <div className="flex items-center gap-2 flex-wrap">
          {wf.map((s, i) => (
            <React.Fragment key={s}>
              <span className="os-chip" style={{ color: i === 0 ? 'var(--os-red)' : 'var(--os-text)', border: '1px solid var(--os-border)' }}>{humanize(s)}</span>
              {i < wf.length - 1 && <ChevronRight className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {sources.map((s) => {
          const d = s.data || {};
          if (s.direct) {
            return (
              <div key={s.key} className="os-card p-4" data-testid={`supply-source-${s.key}`}>
                <div className="flex items-center gap-2"><s.icon className="h-4 w-4" style={{ color: 'var(--os-red)' }} /><h3 className="font-bold" style={{ color: 'var(--os-text)' }}>{s.label}</h3></div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <MetricCard label="Drivers active" value={d.drivers_active} testid="supply-ace-drivers" />
                  <MetricCard label="Fleet vehicles" value={d.fleet_vehicles} testid="supply-ace-vehicles" />
                </div>
              </div>
            );
          }
          const confirmed = d.capacity_confirmed || 0;
          const total = d.total || 0;
          return (
            <div key={s.key} className="os-card p-4" data-testid={`supply-source-${s.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><s.icon className="h-4 w-4" style={{ color: 'var(--os-muted)' }} /><h3 className="font-bold" style={{ color: 'var(--os-text)' }}>{s.label}</h3></div>
                <StatusBadge status={confirmed > 0 ? 'ready' : (total > 0 ? 'unknown' : 'unknown')} label={confirmed > 0 ? `${confirmed} available` : 'Capacity not confirmed'} testid={`supply-cap-${s.key}`} />
              </div>
              <p className="text-3xl os-display mt-3" style={{ color: 'var(--os-text)' }}>{confirmed}<span className="text-base" style={{ color: 'var(--os-muted)' }}> / {total}</span></p>
              <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>confirmed-available / total providers</p>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div><span style={{ color: 'var(--os-muted)' }}>Verified </span><span style={{ color: 'var(--os-text)' }}>{d.verified ?? 0}</span></div>
                <div><span style={{ color: 'var(--os-muted)' }}>Compliant </span><span style={{ color: 'var(--os-text)' }}>{d.compliance_ok ?? 0}</span></div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-4" style={{ color: 'var(--os-muted)' }}>Unknown capacity is never shown as available. Provider ranking follows the P6 waterfall, not the UI.</p>
    </div>
  );
}

/* ══════════════ FLEET ══════════════ */
function FleetView() {
  const navigate = useNavigate();
  const { loading, data, error, reload } = useLoader('/api/pricing/vehicles');
  const [view, setView] = useState('cards');
  const [q, setQ] = useState('');
  const openRec = (v) => navigate(`/os/network/vehicles/${v.vehicle_id}`);
  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState body="Fleet source is unavailable." onRetry={reload} />;
  const vehicles = (data.vehicles || []).filter((v) => !q || (v.vehicle_name || '').toLowerCase().includes(q.toLowerCase()));
  const serviceOf = (v) => (v.status === 'active' || v.available === true) ? 'ready' : (v.status === 'inactive' || v.available === false) ? 'risk' : 'unknown';
  return (
    <div data-testid="fleet-view-panel">
      <Toolbar q={q} setQ={setQ} view={view} setView={setView} testid="fleet" />
      {!vehicles.length ? <EmptyState title="No fleet assets found" /> :
        view === 'table' ? <DataTable testid="fleet-table"
          columns={[{ key: 'vehicle_name', label: 'Vehicle' }, { key: 'category', label: 'Class', render: (r) => humanize(r.category) },
            { key: 'tag', label: 'Ownership', render: (r) => r.ownership || r.tag || '—' }, { key: 'retail_hourly', label: 'Rate', render: (r) => money(r.retail_hourly) },
            { key: 'svc', label: 'Serviceable', render: (r) => <StatusBadge status={serviceOf(r)} /> }]}
          rows={vehicles} onRow={openRec} />
        : view === 'compact' ? <div>{vehicles.map((v) => <CompactRow key={v.vehicle_id} title={v.vehicle_name} subtitle={`${humanize(v.category)} · ${v.ownership || v.tag || 'ACE'}`} status={serviceOf(v)} right={money(v.retail_hourly)} onOpen={() => openRec(v)} />)}</div>
        : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="fleet-list">
          {vehicles.map((v) => <RecordCard key={v.vehicle_id} kind={`Fleet · ${v.vehicle_id}`} title={v.vehicle_name} subtitle={v.ownership || v.tag || 'ACE Owned'} status={serviceOf(v)}
            meta={[{ label: 'Class', value: humanize(v.category) }, { label: 'Capacity', value: `${v.pax || '—'} pax` }, { label: 'Rate', value: money(v.retail_hourly) }, { label: 'Min hrs', value: v.min_hours || '—' }]}
            onOpen={() => openRec(v)} testid={`fleet-card-${v.vehicle_id}`} />)}
        </div>}
    </div>
  );
}

/* ══════════════ DRIVERS ══════════════ */
function DriversView() {
  const navigate = useNavigate();
  const { loading, data, error, reload } = useLoader('/api/command-center/drivers');
  const [q, setQ] = useState('');
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Driver records are unavailable." onRetry={reload} />;
  const drivers = (data.drivers || []).filter((d) => !q || (d.name || '').toLowerCase().includes(q.toLowerCase()));
  const statusOf = (d) => d.status === 'available' ? 'ready' : d.status === 'inactive' ? 'risk' : d.status ? 'waiting' : 'unknown';
  return (
    <div data-testid="drivers-view">
      <Toolbar q={q} setQ={setQ} testid="drivers" />
      {!drivers.length ? <EmptyState title="No drivers on record" /> :
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="drivers-list">
          {drivers.map((d) => <RecordCard key={d.driver_id} kind={`Driver · ${d.driver_id}`} title={d.name || 'Driver'} subtitle={d.primary_vehicle || '—'} status={statusOf(d)}
            meta={[{ label: 'Status', value: d.status ? humanize(d.status) : 'Unknown' }, { label: 'Trips', value: d.total_trips ?? '—' }]}
            onOpen={() => navigate(`/os/network/drivers/${d.driver_id}`)} testid={`driver-card-${d.driver_id}`} />)}
        </div>}
    </div>
  );
}

/* ══════════════ PARTNERS ══════════════ */
function PartnersView() {
  const navigate = useNavigate();
  const { loading, data, error, reload } = useLoader('/api/fulfillment/control/providers');
  const [q, setQ] = useState('');
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Provider directory is unavailable." onRetry={reload} />;
  const providers = (data.providers || []).filter((p) => !q || (p.provider_name || '').toLowerCase().includes(q.toLowerCase()));
  const compStatus = (p) => CMP_STATUS[p.compliance_signal] || 'unknown';
  return (
    <div data-testid="partners-view">
      <Toolbar q={q} setQ={setQ} testid="partners" />
      {!providers.length ? <EmptyState title="No partners on record" /> :
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="partners-list">
          {providers.map((p) => {
            const isAff = p.provider_type === 'affiliate_partner';
            return (
              <div key={p.provider_id} className="os-card p-4 cursor-pointer" onClick={() => navigate(`/os/network/partners/${p.provider_id}`)} data-testid={`partner-card-${p.provider_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="os-chip" style={{ color: isAff ? 'var(--os-info)' : 'var(--os-warning)', border: '1px solid var(--os-border)' }}>{isAff ? 'Affiliate · AFF' : 'Fleet Owner · OWN'}</span>
                    <h3 className="font-bold text-base mt-1 truncate" style={{ color: 'var(--os-text)' }}>{p.provider_name}</h3>
                    <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{humanize(p.preferred_tier)} tier · {p.provider_id}</p>
                  </div>
                  <StatusBadge status={compStatus(p)} label={humanize(p.compliance_signal)} />
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(p.fulfillment_capabilities || []).slice(0, 4).map((c) => <span key={c} className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{humanize(c)}</span>)}
                  {!(p.fulfillment_capabilities || []).length && <span className="text-xs" style={{ color: 'var(--os-muted)' }}>No approved capabilities</span>}
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs" style={{ color: 'var(--os-muted)' }}>
                  <span>Verified: {p.verification_status === 'approved' ? '✓' : '—'}</span>
                  <span>Capacity: {p.capacity_status === 'available' ? 'confirmed' : 'unknown'}</span>
                </div>
              </div>
            );
          })}
        </div>}
    </div>
  );
}

function PerformanceBlock({ perf }) {
  if (!perf) return <UnknownState title="No performance data" />;
  const rows = [
    ['Requests received', perf.requests_received], ['Response rate', perf.response_rate],
    ['Acceptance rate', perf.acceptance_rate], ['Avg response (hrs)', perf.avg_response_hours],
    ['Confirmation rate', perf.confirmation_rate], ['Completion rate', perf.completion_rate],
    ['Cancellation rate', perf.cancellation_rate], ['Dispute rate', perf.dispute_rate],
  ];
  const fmt = (m) => m?.value === null || m?.value === undefined ? '—' : (String(m.value).includes('.') && m.value <= 1 && m.value >= 0 ? `${Math.round(m.value * 100)}%` : m.value);
  return (
    <div data-testid="partner-performance">
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>P7 read model · {perf.window_days}-day window. Sample size (n) shown per metric — not a global 1–100 score.</p>
      <div className="grid grid-cols-2 gap-2">
        {rows.map(([label, m]) => (
          <div key={label} className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p>
            <p className="os-display text-2xl" style={{ color: m?.value === null || m?.value === undefined ? 'var(--os-muted)' : 'var(--os-text)' }}>{fmt(m)}</p>
            <p className="text-[10px]" style={{ color: 'var(--os-muted)' }}>n={m?.sample_size ?? 0}</p></div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════ FULFILLMENT — Sourcing Requests + Award (R4C.1) ══════════════ */
const FUL_VIEWS = [
  { id: 'requests', label: 'Requests' }, { id: 'awaiting', label: 'Awaiting Response' },
  { id: 'awardready', label: 'Award Ready' }, { id: 'awarded', label: 'Awarded' },
  { id: 'service', label: 'In Service / Completion' }, { id: 'recovery', label: 'Recovery' },
];
const BUCKET_OF_VIEW = { requests: 'needs_fulfillment', awaiting: 'awaiting_response', awardready: 'award_ready', awarded: 'awarded' };
const BUCKET_STATUS = { needs_fulfillment: 'attention', awaiting_response: 'waiting', award_ready: 'ready', awarded: 'ready', blocked_reopened: 'risk' };
const OUTSOURCE_REASONS = ['NO_CAPACITY', 'CAPABILITY_MISSING', 'MARKET_OUTSIDE_DIRECT', 'ASSET_UNAVAILABLE', 'COMPLIANCE_CONSTRAINT', 'ECONOMIC_REVIEW', 'CLIENT_APPROVED_REQUIREMENT', 'OTHER_APPROVED'];
// board-row classification (used only by In Service / Completion + Recovery operational lists)
const tagKind = (r) => r.dispute_id ? 'dispute' : r.compliance_id ? 'compliance' : (r.reconciliation_id && r.status) ? 'payout' : 'fulfillment';
const KIND_TITLE = { dispute: 'Dispute', compliance: 'Compliance', payout: 'Payout approval', fulfillment: 'Fulfillment' };

function FulfillmentView({ doConfirm, runAction, bk, frq, navigate }) {
  const feed = useLoader('/api/fulfillment/requests');
  const board = useLoader('/api/fulfillment/control/board');
  const [view, setView] = useState('requests');
  const [peekId, setPeekId] = useState(null);   // FRQ id (peek object derived from live feed)
  const [boardPeek, setBoardPeek] = useState(null);
  const deepLinkApplied = useRef(false);
  const requests = useMemo(() => feed.data?.requests || [], [feed.data]);
  const counts = feed.data?.counts || {};
  const peek = useMemo(() => requests.find((r) => r.fulfillment_request_id === peekId) || null, [requests, peekId]);

  // deep-link: open exact FRQ (or by booking_id) exactly once
  useEffect(() => {
    if (deepLinkApplied.current || !requests.length || (!frq && !bk)) return;
    const r = frq ? requests.find((x) => x.fulfillment_request_id === frq) : requests.find((x) => x.booking_id === bk);
    if (r) { setView(mapBucketToView(r.bucket)); setPeekId(r.fulfillment_request_id); }
    deepLinkApplied.current = true;
  }, [frq, bk, requests]);

  if ((feed.loading && !feed.data) || (board.loading && !board.data)) return <LoadingState rows={4} />;
  if (feed.error) return <ErrorState body="Sourcing requests feed is unavailable." onRetry={feed.reload} />;
  const reloadAll = () => { feed.reload(); board.reload(); };

  const boardLists = {
    service: (board.data?.completion_evidence || []).map((x) => ({ ...x, _kind: 'fulfillment' })),
    recovery: [
      ...requests.filter((r) => r.bucket === 'blocked_reopened').map((r) => ({ ...r, _kind: 'request' })),
      ...(board.data?.disputes || []).map((x) => ({ ...x, _kind: 'dispute' })),
      ...(board.data?.compliance || []).filter((c) => c.effective_status === 'expired').map((x) => ({ ...x, _kind: 'compliance' })),
    ],
  };
  const bucket = BUCKET_OF_VIEW[view];
  const reqRows = bucket ? requests.filter((r) => r.bucket === bucket) : [];
  const boardRows = boardLists[view] || [];

  const resolveDispute = (id) => doConfirm({ title: 'Resolve dispute?', body: 'Restores the linked reconciliation to APPROVED via the canonical DSP workflow.',
    onConfirm: () => runAction(`/api/fulfillment/control/disputes/${id}/resolve`, { authorized_by: AUTHZ, resolution: 'reviewed', restore_status: 'APPROVED' }, () => { reloadAll(); setBoardPeek(null); }) });

  return (
    <div data-testid="fulfillment-view">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {['needs_fulfillment', 'awaiting_response', 'award_ready', 'awarded', 'blocked_reopened'].map((k) => (
          <StatusBadge key={k} status={counts[k] ? BUCKET_STATUS[k] : 'ready'} label={`${humanize(k)} ${counts[k] || 0}`} testid={`ful-count-${k}`} />
        ))}
      </div>
      <ContextTabs tabs={FUL_VIEWS} active={view} onChange={setView} testid="fulfillment-subtabs" />
      <div className="mt-3">
        {bucket ? (
          !reqRows.length ? <EmptyState title="Nothing here right now" body={view === 'requests' ? 'No requirements currently need sourcing.' : undefined} />
            : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="fulfillment-list">
              {reqRows.map((r) => <RequestCard key={r.fulfillment_request_id} r={r} onOpen={() => setPeekId(r.fulfillment_request_id)} />)}
            </div>
        ) : (
          !boardRows.length ? <EmptyState title="Nothing here right now" body={view === 'recovery' ? 'No reopened requests, disputes or failed-service items. Positive operational state.' : 'No in-service items awaiting completion evidence.'} />
            : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="fulfillment-list">
              {boardRows.map((r, i) => r._kind === 'request'
                ? <RequestCard key={r.fulfillment_request_id} r={r} onOpen={() => { setView('recovery'); setPeekId(r.fulfillment_request_id); }} />
                : <FulfillmentCard key={r.offer_id || r.dispute_id || r.compliance_id || i} row={r} onOpen={() => setBoardPeek(r)} />)}
            </div>
        )}
      </div>

      {peek && <RequestPeek request={peek} onClose={() => setPeekId(null)} doConfirm={doConfirm} runAction={runAction} reloadAll={reloadAll} navigate={navigate} />}

      <DetailDrawer open={!!boardPeek} title={boardPeek ? (KIND_TITLE[boardPeek._kind] || 'Item') : ''}
        subtitle={boardPeek ? `${boardPeek.provider_name || boardPeek.provider_id || ''}${boardPeek.booking_id ? ` · ${boardPeek.booking_id}` : ''}` : ''} onClose={() => setBoardPeek(null)} testid="fulfillment-board-peek"
        footer={boardPeek?._kind === 'dispute' ? <ActionBar primary={{ label: 'Resolve dispute', onClick: () => resolveDispute(boardPeek.dispute_id) }} /> : null}>
        {boardPeek && <div>
          <PeekField label="Type" value={KIND_TITLE[boardPeek._kind]} />
          <PeekField label="Requirement" value={humanize(boardPeek.requirement_type)} />
          <PeekField label="Canonical booking" value={boardPeek.booking_id} />
          <PeekField label="Provider" value={boardPeek.provider_name || boardPeek.provider_id} />
          <PeekField label="Service state" value={humanize(boardPeek.service_state)} />
          <PeekField label="Partner cost (internal)" value={money(boardPeek.partner_cost)} />
          {boardPeek._kind === 'dispute' && <PeekField label="Dispute reason" value={boardPeek.reason} />}
          <PeekField label="What ACE must do next" value={humanize(boardPeek.required_action)} />
        </div>}
      </DetailDrawer>
    </div>
  );
}
function mapBucketToView(b) { return { needs_fulfillment: 'requests', awaiting_response: 'awaiting', award_ready: 'awardready', awarded: 'awarded', blocked_reopened: 'recovery' }[b] || 'requests'; }

function RequestCard({ r, onOpen }) {
  const st = BUCKET_STATUS[r.bucket] || 'unknown';
  const os = r.offer_summary || {};
  return (
    <div className="os-card p-4 cursor-pointer" onClick={onOpen} style={{ borderLeft: `3px solid ${st === 'risk' ? 'var(--os-danger)' : st === 'ready' ? 'var(--os-success)' : 'var(--os-warning)'}` }} data-testid={`request-card-${r.fulfillment_request_id}`}>
      <div className="flex items-center justify-between">
        <StatusBadge status={st} label={humanize(r.bucket)} />
        <span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{r.fulfillment_request_id}</span>
      </div>
      <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{humanize(r.requirement_type)}</h3>
      <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>
        {[r.booking_id || r.canonical_demand_id || 'no booking', r.market, r.service_date].filter(Boolean).join(' · ')}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px]" style={{ color: 'var(--os-muted)' }}>
        <span>Stage: <span style={{ color: 'var(--os-text)' }}>{humanize(r.current_waterfall_stage)}</span></span>
        {r.outsource_reason && <span>Reason: {humanize(r.outsource_reason)}</span>}
        {r.response_deadline && <span>Deadline: {r.response_deadline}</span>}
        <span>Offers: {os.total || 0}</span>
        {r.current_provider_name && <span>Provider: <span style={{ color: 'var(--os-text)' }}>{r.current_provider_name}</span></span>}
      </div>
      <p className="text-xs mt-2" style={{ color: 'var(--os-red)' }}>{r.required_action}</p>
    </div>
  );
}

function RequestPeek({ request, onClose, doConfirm, runAction, reloadAll, navigate }) {
  const rid = request.fulfillment_request_id;
  const [ptab, setPtab] = useState('overview');
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const cand = useLoader(`/api/fulfillment/requests/${rid}/candidates`);
  const offers = useLoader(`/api/fulfillment/requests/${rid}/offers`);
  const hist = useLoader(`/api/fulfillment/requests/${rid}/awards`);
  const refresh = () => { cand.reload(); offers.reload(); hist.reload(); reloadAll(); };

  const advance = (reason) => { setAdvanceOpen(false); doConfirm({ title: 'Advance waterfall?', body: `Move ${rid} to the next tier with reason ${humanize(reason)} (P6 governed).`,
    onConfirm: () => runAction(`/api/fulfillment/requests/${rid}/advance`, { outsource_reason: reason, by: AUTHZ }, refresh) }); };
  const requestOffer = (c) => doConfirm({ title: 'Request offer?', body: `Send a sourcing request to ${c.provider_name} (${c.provider_id}). Auto-award stays OFF.`,
    onConfirm: () => runAction(`/api/fulfillment/requests/${rid}/offers`, { provider_id: c.provider_id }, refresh) });
  const respond = (o, status) => runAction(`/api/fulfillment/offers/${o.offer_id}/response`, { status }, refresh);
  const accept = (o) => { const c = window.prompt('Accepted partner cost ($, internal). Required to award later:', o.proposed_cost ?? ''); if (c === null) return; const n = Number(c); if (c !== '' && Number.isNaN(n)) { toast.error('Enter a valid number'); return; } runAction(`/api/fulfillment/offers/${o.offer_id}/response`, { status: 'ACCEPTED', ...(c !== '' ? { proposed_cost: n } : {}) }, refresh); };
  const counter = (o) => { const c = window.prompt('Counter partner cost ($, internal)?'); if (c === null) return; const n = Number(c); if (c === '' || Number.isNaN(n)) { toast.error('Enter a valid number'); return; } runAction(`/api/fulfillment/offers/${o.offer_id}/response`, { status: 'COUNTERED', proposed_cost: n }, refresh); };
  const award = (o) => { if (o.proposed_cost === null || o.proposed_cost === undefined) { toast.error('Set a partner cost before awarding'); return; } doConfirm({ title: 'Award provider?', body: `Award ${o.provider_name} at partner cost ${money(o.proposed_cost)}. Preserves the canonical booking; auto-award is disabled.`,
    onConfirm: () => runAction(`/api/fulfillment/offers/${o.offer_id}/award`, { authorized_by: AUTHZ, agreed_cost: o.proposed_cost, assigned_asset: o.asset_details || '' }, refresh) }); };
  const reopen = () => doConfirm({ title: 'Reopen request?', danger: true, body: 'Cancels the current award and re-opens sourcing. Award history is preserved; the client promise (date/vehicle/total) is untouched.',
    onConfirm: () => runAction(`/api/fulfillment/requests/${rid}/reopen`, { reason: 'provider_unavailable', by: AUTHZ }, () => { refresh(); onClose(); }) });

  const isAwarded = request.status === 'awarded';
  const footer = (
    <ActionBar
      primary={isAwarded ? { label: 'Reopen', onClick: reopen } : { label: 'Advance Waterfall', onClick: () => setAdvanceOpen(true) }}
      secondary={[
        ...(request.booking_id ? [{ label: 'Open Service Record', onClick: () => navigate(`/os/services/${request.booking_id}`) }] : []),
      ]} />
  );

  return (
    <>
      <DetailDrawer open title={humanize(request.requirement_type)} subtitle={`${rid}${request.booking_id ? ` · ${request.booking_id}` : ''}`}
        tabs={[{ id: 'overview', label: 'Overview' }, { id: 'candidates', label: 'Candidates' }, { id: 'offers', label: `Offers ${offers.data?.total ? `(${offers.data.total})` : ''}` }, { id: 'history', label: 'Award / History' }]}
        activeTab={ptab} onTab={setPtab} onClose={onClose} testid="request-peek" footer={footer}>

        {ptab === 'overview' && <div data-testid="request-overview">
          <div className="flex gap-2 mb-3"><StatusBadge status={BUCKET_STATUS[request.bucket] || 'unknown'} label={humanize(request.bucket)} /></div>
          <PeekField label="Requirement" value={humanize(request.requirement_type)} />
          <PeekField label="Canonical demand" value={request.booking_id || request.canonical_demand_id} />
          <PeekField label="Market" value={request.market} />
          <PeekField label="Service date / window" value={[request.service_date, request.service_window].filter(Boolean).join(' · ')} />
          <PeekField label="Passenger capacity" value={request.passenger_capacity} />
          <PeekField label="Asset / vehicle requirement" value={request.vehicle_or_asset_requirement} />
          <p className="text-[10px] uppercase tracking-wider font-bold mt-4 mb-2" style={{ color: 'var(--os-muted)' }}>Waterfall</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {['ACE_DIRECT', 'PREFERRED_AFFILIATE', 'DRIVEN_COLLECTIVE', 'SPECIAL_SOURCE'].map((s, i, arr) => (
              <React.Fragment key={s}>
                <span className="os-chip" style={s === request.current_waterfall_stage ? { background: 'var(--os-red)', color: '#fff' } : { color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{humanize(s)}</span>
                {i < arr.length - 1 && <ChevronRight className="h-3 w-3" style={{ color: 'var(--os-muted)' }} />}
              </React.Fragment>
            ))}
          </div>
          {request.outsource_reason && <PeekField label="Outsource reason" value={humanize(request.outsource_reason)} />}
          <p className="text-xs mt-4" style={{ color: 'var(--os-red)' }}>{request.required_action}</p>
        </div>}

        {ptab === 'candidates' && <div data-testid="request-candidates">
          {cand.loading ? <LoadingState rows={2} /> : cand.error ? <ErrorState body="Candidates unavailable" onRetry={cand.reload} />
            : cand.data?.ace_direct ? <div className="os-card p-4">
                <p className="font-bold" style={{ color: 'var(--os-text)' }}>ACE Direct evaluated first</p>
                <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{cand.data.ace_direct.drivers_active} drivers active · {cand.data.ace_direct.fleet_vehicles} fleet vehicles.</p>
                <p className="text-xs mt-2" style={{ color: 'var(--os-muted)' }}>{cand.data.ace_direct.note}</p>
              </div>
            : !(cand.data?.candidates || []).length ? <EmptyState title="No eligible candidates at this stage" body="Advance the waterfall to widen the provider pool." />
            : <div className="space-y-2">
              <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>P6 eligible candidates for stage {humanize(cand.data.stage)} (ranked by P6, not the UI).</p>
              {(cand.data.candidates).map((c) => <div key={c.provider_id} className="os-card p-3" data-testid={`candidate-${c.provider_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{c.provider_name}</p>
                    <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{humanize(c.tier)} · {c.provider_id}</p></div>
                  <StatusBadge status={c.capacity_state === 'CAPACITY_CONFIRMED' ? 'ready' : 'unknown'} label={c.capacity_state === 'CAPACITY_CONFIRMED' ? 'Capacity confirmed' : 'Capacity unknown'} />
                </div>
                <div className="flex flex-wrap gap-1 mt-2">{(c.reasons || []).map((rs, i) => <span key={i} className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{rs}</span>)}</div>
                <button className="os-btn-primary mt-3 w-full justify-center" style={{ minHeight: 36 }} onClick={() => requestOffer(c)} data-testid={`request-offer-${c.provider_id}`}>Request Offer</button>
              </div>)}
            </div>}
        </div>}

        {ptab === 'offers' && <div data-testid="request-offers">
          {offers.loading ? <LoadingState rows={2} /> : !(offers.data?.offers || []).length ? <EmptyState title="No offers yet" body="Request an offer from an eligible candidate." />
            : <div className="space-y-2">
              <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>Offer comparison — partner cost is internal only and is never shown to partners. Client price / margin is not calculated here.</p>
              {(offers.data.offers).map((o) => <div key={o.offer_id} className="os-card p-3" data-testid={`offer-${o.offer_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{o.provider_name}</p>
                    <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{o.provider_type === 'affiliate_partner' ? 'Affiliate' : 'Fleet Owner'} · {o.offer_id}</p></div>
                  <StatusBadge status={offerStatus(o.status)} label={humanize(o.status)} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span style={{ color: 'var(--os-muted)' }}>Partner cost </span><span style={{ color: 'var(--os-text)' }}>{money(o.proposed_cost)}</span></div>
                  <div><span style={{ color: 'var(--os-muted)' }}>Asset </span><span style={{ color: 'var(--os-text)' }}>{o.asset_details || '—'}</span></div>
                </div>
                {o.notes && <p className="text-[11px] mt-1" style={{ color: 'var(--os-muted)' }}>Notes: {o.notes}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  {['REQUESTED', 'VIEWED'].includes(o.status) && <>
                    <button className="os-btn-ghost" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => accept(o)} data-testid={`offer-accept-${o.offer_id}`}>Mark Accepted</button>
                    <button className="os-btn-ghost" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => counter(o)} data-testid={`offer-counter-${o.offer_id}`}>Counter</button>
                    <button className="os-btn-ghost" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => respond(o, 'DECLINED')} data-testid={`offer-decline-${o.offer_id}`}>Decline</button>
                  </>}
                  {['ACCEPTED', 'COUNTERED'].includes(o.status) && <button className="os-btn-primary" style={{ minHeight: 34, padding: '6px 14px', ...(o.proposed_cost === null || o.proposed_cost === undefined ? { opacity: 0.5 } : {}) }} onClick={() => award(o)} data-testid={`offer-award-${o.offer_id}`}>Award Provider</button>}
                  {o.status === 'AWARDED' && <StatusBadge status="ready" label="Awarded" />}
                </div>
              </div>)}
            </div>}
        </div>}

        {ptab === 'history' && <div data-testid="request-history">
          {hist.loading ? <LoadingState rows={2} /> : <>
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Awards</p>
            {(hist.data?.awards || []).length ? (hist.data.awards).map((a) => <div key={a.award_id} className="os-card p-3 mb-2">
              <p className="text-sm font-semibold" style={{ color: 'var(--os-text)' }}>{a.provider_id} · {money(a.agreed_cost)}</p>
              <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{a.award_id} · {humanize(a.confirmation_status)} · {humanize(a.service_state)}</p>
            </div>) : <p className="text-xs" style={{ color: 'var(--os-muted)' }}>No awards yet.</p>}
            <p className="text-[10px] uppercase tracking-wider font-bold mt-4 mb-2" style={{ color: 'var(--os-muted)' }}>History</p>
            {(hist.data?.history || []).slice().reverse().map((h, i) => <p key={i} className="text-xs" style={{ color: 'var(--os-muted)' }}>{humanize(h.event)} · {(h.at || '').slice(0, 16).replace('T', ' ')}{h.reason ? ` · ${humanize(h.reason)}` : ''}{h.by ? ` · ${h.by}` : ''}</p>)}
          </>}
        </div>}
      </DetailDrawer>

      <ActionSheet open={advanceOpen} title="Advance waterfall — reason" onClose={() => setAdvanceOpen(false)}
        actions={OUTSOURCE_REASONS.map((r) => ({ label: humanize(r), onClick: () => advance(r) }))} testid="advance-sheet" />
    </>
  );
}
const offerStatus = (s) => ({ REQUESTED: 'waiting', VIEWED: 'waiting', ACCEPTED: 'ready', COUNTERED: 'attention', DECLINED: 'risk', EXPIRED: 'risk', AWARDED: 'ready', CANCELLED: 'unknown' }[s] || 'unknown');

function FulfillmentCard({ row, onOpen }) {
  const kind = row._kind || tagKind(row);
  const status = (kind === 'dispute' || kind === 'compliance') ? 'risk' : kind === 'payout' ? 'attention' : (row.confirmation_status === 'CONFIRMATION_OVERDUE' ? 'risk' : 'attention');
  const title = kind === 'fulfillment' ? (humanize(row.requirement_type) || 'Fulfillment') : `${KIND_TITLE[kind]}${row.compliance_id && row.capability ? ` · ${humanize(row.capability)}` : ''}`;
  return (
    <div className="os-card p-4 cursor-pointer" onClick={onOpen} style={{ borderLeft: `3px solid ${status === 'risk' ? 'var(--os-danger)' : 'var(--os-warning)'}` }} data-testid={`ful-card-${row.offer_id || row.dispute_id || row.compliance_id || row.reconciliation_id || 'x'}`}>
      <div className="flex items-center justify-between"><StatusBadge status={status} label={KIND_TITLE[kind]} />{row.booking_id && <span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{row.booking_id}</span>}</div>
      <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{title}</h3>
      <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{row.provider_name || row.provider_id || ''}{row.market ? ` · ${row.market}` : ''}</p>
      <p className="text-xs mt-2" style={{ color: 'var(--os-red)' }}>{humanize(row.required_action) || 'Review'}</p>
    </div>
  );
}


/* ══════════════ COMPLIANCE ══════════════ */
const CMP_FILTERS = [
  { id: 'needs', label: 'Needs Review' }, { id: 'expiring', label: 'Expiring' }, { id: 'expired', label: 'Expired' }, { id: 'current', label: 'Current' },
];
function ComplianceView({ doConfirm, runAction }) {
  const { loading, data, error, reload } = useLoader('/api/fulfillment/control/compliance');
  const [filter, setFilter] = useState('needs');
  const [peek, setPeek] = useState(null);
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Compliance view is unavailable." onRetry={reload} />;
  const all = data.compliance || [];
  const bucket = (c) => {
    const s = c.effective_status;
    if (s === 'expired') return 'expired';
    if (s === 'expiring') return 'expiring';
    if (['approved', 'valid', 'current'].includes(s)) return 'current';
    return 'needs';
  };
  const counts = all.reduce((acc, c) => { acc[bucket(c)] = (acc[bucket(c)] || 0) + 1; return acc; }, {});
  const rows = all.filter((c) => bucket(c) === filter);
  const review = (c, status) => doConfirm({ title: status === 'approved' ? 'Clear compliance?' : 'Reject compliance?', danger: status === 'rejected',
    body: `${status === 'approved' ? 'Approve/clear' : 'Reject'} ${c.type || 'document'} for ${c.provider_name}. Uses canonical compliance review.`,
    onConfirm: () => runAction(`/api/fulfillment/control/providers/${c.provider_id}/compliance/${c.compliance_id}/review`, { review_status: status, reviewer: AUTHZ }, () => { reload(); setPeek(null); }) });
  return (
    <div data-testid="compliance-view">
      <ContextTabs tabs={CMP_FILTERS.map((f) => ({ ...f, count: counts[f.id] || 0 }))} active={filter} onChange={setFilter} testid="compliance-filters" />
      <div className="mt-3">
        {!rows.length ? <EmptyState title="Nothing in this bucket" body={filter === 'expired' ? 'No expired documents. Positive state.' : undefined} />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="compliance-list">
            {rows.map((c) => <div key={c.compliance_id} className="os-card p-4 cursor-pointer" onClick={() => setPeek(c)} data-testid={`compliance-card-${c.compliance_id}`}>
              <div className="flex items-center justify-between"><StatusBadge status={CMP_STATUS[c.effective_status] || 'unknown'} label={humanize(c.effective_status)} /><span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{c.compliance_id}</span></div>
              <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{c.provider_name}</h3>
              <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{humanize(c.type)} · Affects: <span style={{ color: 'var(--os-text)' }}>{humanize(c.capability) || 'all'}</span></p>
              <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>Expiry: {c.expiry_date || '—'}</p>
            </div>)}
          </div>}
      </div>
      <DetailDrawer open={!!peek} title={peek?.provider_name} subtitle={peek ? `Compliance · ${peek.compliance_id}` : ''} onClose={() => setPeek(null)} testid="compliance-peek"
        footer={peek ? <ActionBar primary={{ label: 'Clear / Approve', onClick: () => review(peek, 'approved') }} secondary={[{ label: 'Reject', onClick: () => review(peek, 'rejected') }]} /> : null}>
        {peek && <div>
          <div className="flex gap-2 mb-3"><StatusBadge status={CMP_STATUS[peek.effective_status] || 'unknown'} label={humanize(peek.effective_status)} /></div>
          <PeekField label="Document type" value={humanize(peek.type)} />
          <PeekField label="Capability affected" value={humanize(peek.capability) || 'All capabilities'} />
          <PeekField label="Expiry date" value={peek.expiry_date} />
          <PeekField label="Review status" value={humanize(peek.review_status || peek.status)} />
          <p className="text-[11px] mt-3" style={{ color: 'var(--os-muted)' }}>Impact is capability-specific — one invalid document does not blanket-disable a provider unless P7 marks it so.</p>
        </div>}
      </DetailDrawer>
    </div>
  );
}

/* ══════════════ PAYOUTS / RECONCILIATION ══════════════ */
const PAY_FILTERS = [
  { id: 'NOT_READY', label: 'Not Ready' }, { id: 'READY', label: 'Ready' }, { id: 'HOLD_REVIEW', label: 'Hold' },
  { id: 'APPROVED', label: 'Approved' }, { id: 'SCHEDULED', label: 'Scheduled' }, { id: 'PAID', label: 'Paid' }, { id: 'DISPUTED', label: 'Disputed' },
];
function PayoutsView({ doConfirm, runAction, openRec, initialFilter }) {
  const { loading, data, error, reload } = useLoader('/api/fulfillment/control/reconciliations');
  const providers = useLoader('/api/fulfillment/control/providers');
  const validFilters = PAY_FILTERS.map((f) => f.id);
  const wanted = (initialFilter || '').toUpperCase();
  const [filter, setFilter] = useState(validFilters.includes(wanted) ? wanted : 'READY');
  const [peek, setPeek] = useState(null);
  const nameMap = useMemo(() => Object.fromEntries((providers.data?.providers || []).map((p) => [p.provider_id, p.provider_name])), [providers.data]);
  const recs = useMemo(() => (data?.reconciliations || []), [data]);
  useEffect(() => { if (openRec && recs.length) { const r = recs.find((x) => x.reconciliation_id === openRec); if (r) setPeek(r); } }, [openRec, recs]);
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Reconciliation records are unavailable." onRetry={reload} />;
  const counts = recs.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const rows = recs.filter((r) => r.status === filter);
  const name = (r) => nameMap[r.provider_id] || r.provider_id;

  const approve = (r) => doConfirm({ title: 'Approve payout?', body: `Approve ${r.reconciliation_id} (${money(r.gross_payable)}) for Finance progression. Material decision.`,
    onConfirm: () => runAction(`/api/fulfillment/control/reconciliations/${r.reconciliation_id}/approve`, { authorized_by: AUTHZ }, () => { reload(); setPeek(null); }) });
  const hold = (r) => { const reason = window.prompt('Hold reason?'); if (reason) runAction(`/api/fulfillment/control/reconciliations/${r.reconciliation_id}/hold`, { reason }, () => { reload(); setPeek(null); }); };
  const schedule = (r) => { const when = window.prompt('Scheduled-for date (YYYY-MM-DD)?', ''); if (when !== null) runAction(`/api/fulfillment/control/reconciliations/${r.reconciliation_id}/schedule`, { scheduled_for: when }, () => { reload(); setPeek(null); }); };
  const adjust = (r) => { const amt = window.prompt('Adjustment amount (+/-)?'); if (amt === null) return; const reason = window.prompt('Adjustment reason (required)?'); if (!reason) { toast.error('Reason required'); return; } runAction(`/api/fulfillment/control/reconciliations/${r.reconciliation_id}/adjust`, { amount: Number(amt), reason }, () => { reload(); }); };
  const markPaid = (r) => { const ref = window.prompt('Finance / payment reference (required to mark PAID):'); if (!ref) return; doConfirm({ title: 'Mark PAID?', danger: true, body: `Confirm ${r.reconciliation_id} is PAID per Finance evidence (${ref}). Irreversible.`, confirmLabel: 'Mark Paid',
    onConfirm: () => runAction(`/api/fulfillment/control/reconciliations/${r.reconciliation_id}/mark-paid`, { finance_ref: ref }, () => { reload(); setPeek(null); }) }); };

  const actionsFor = (r) => {
    switch (r.status) {
      case 'READY': return [{ label: 'Approve', primary: true, onClick: () => approve(r) }, { label: 'Hold', onClick: () => hold(r) }, { label: 'Adjust', onClick: () => adjust(r) }];
      case 'HOLD_REVIEW': return [{ label: 'Approve', primary: true, onClick: () => approve(r) }, { label: 'Adjust', onClick: () => adjust(r) }];
      case 'APPROVED': return [{ label: 'Mark Paid', primary: true, onClick: () => markPaid(r) }, { label: 'Schedule', onClick: () => schedule(r) }];
      case 'SCHEDULED': return [{ label: 'Mark Paid', primary: true, onClick: () => markPaid(r) }];
      case 'DISPUTED': return [{ label: 'Resolve in Fulfillment', onClick: () => toast.message('Resolve disputes from Fulfillment → Recovery') }];
      default: return [];
    }
  };
  const acts = peek ? actionsFor(peek) : [];
  return (
    <div data-testid="payouts-view">
      <ContextTabs tabs={PAY_FILTERS.map((f) => ({ ...f, count: counts[f.id] || 0 }))} active={filter} onChange={setFilter} testid="payouts-filters" />
      <div className="mt-3">
        {!rows.length ? <EmptyState title="No reconciliations in this state" />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="payouts-list">
            {rows.map((r) => <div key={r.reconciliation_id} className="os-card p-4 cursor-pointer" onClick={() => setPeek(r)} data-testid={`payout-card-${r.reconciliation_id}`}>
              <div className="flex items-center justify-between"><StatusBadge status={REC_STATUS[r.status] || 'unknown'} label={humanize(r.status)} /><span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{r.reconciliation_id}</span></div>
              <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{name(r)}</h3>
              <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{r.booking_id || '—'}</p>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><p className="text-[10px] uppercase font-bold" style={{ color: 'var(--os-muted)' }}>Partner cost</p><p style={{ color: 'var(--os-text)' }}>{money(r.awarded_partner_cost)}</p></div>
                <div><p className="text-[10px] uppercase font-bold" style={{ color: 'var(--os-muted)' }}>Adjustment</p><p style={{ color: 'var(--os-text)' }}>{money(r.approved_adjustments)}</p></div>
                <div><p className="text-[10px] uppercase font-bold" style={{ color: 'var(--os-muted)' }}>Payable</p><p style={{ color: 'var(--os-red)' }}>{money(r.gross_payable)}</p></div>
              </div>
              {r.hold_review_reason && <p className="text-[11px] mt-2" style={{ color: 'var(--os-warning)' }}>Hold: {r.hold_review_reason}</p>}
            </div>)}
          </div>}
      </div>
      <DetailDrawer open={!!peek} title={peek ? name(peek) : ''} subtitle={peek ? `Reconciliation · ${peek.reconciliation_id}` : ''} onClose={() => setPeek(null)} testid="payout-peek"
        footer={acts.length ? <ActionBar primary={acts.find((a) => a.primary) ? { label: acts.find((a) => a.primary).label, onClick: acts.find((a) => a.primary).onClick } : undefined}
          secondary={acts.filter((a) => !a.primary).map((a) => ({ label: a.label, onClick: a.onClick }))} /> : null}>
        {peek && <div>
          <div className="flex gap-2 mb-3"><StatusBadge status={REC_STATUS[peek.status] || 'unknown'} label={humanize(peek.status)} /></div>
          <PeekField label="Linked booking" value={peek.booking_id} />
          <PeekField label="Partner cost / award" value={money(peek.awarded_partner_cost)} />
          <PeekField label="Authorized adjustment" value={money(peek.approved_adjustments)} />
          <PeekField label="Approved payable" value={money(peek.gross_payable)} />
          {peek.hold_review_reason && <PeekField label="Hold reason" value={peek.hold_review_reason} />}
          <p className="text-[11px] mt-3" style={{ color: 'var(--os-muted)' }}>Client price is never shown here. PAID requires Finance evidence. Payouts are not recomputed in the UI.</p>
          {(peek.history || []).length > 0 && <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--os-muted)' }}>History</p>
            {(peek.history || []).slice(-8).reverse().map((h, i) => <p key={i} className="text-xs" style={{ color: 'var(--os-muted)' }}>{humanize(h.event)} · {(h.at || '').slice(0, 10)}{h.by ? ` · ${h.by}` : ''}{h.reason ? ` · ${h.reason}` : ''}</p>)}
          </div>}
        </div>}
      </DetailDrawer>
    </div>
  );
}

/* ══════════════ shared bits ══════════════ */
function Toolbar({ q, setQ, view, setView, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-4">
      <div className="flex items-center gap-2 os-input" style={{ minHeight: 40, maxWidth: 280 }}>
        <Search className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" data-testid={`${testid}-search`} className="bg-transparent outline-none text-sm flex-1" style={{ color: 'var(--os-text)' }} />
      </div>
      {view !== undefined && <ViewToggle view={view} onChange={setView} testid={`${testid}-view`} />}
    </div>
  );
}
const PeekField = ({ label, value }) => (
  <div className="mb-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p>
    <p className="text-sm" style={{ color: 'var(--os-text)' }}>{value === undefined || value === null || value === '' ? '—' : value}</p></div>
);
